"use client";

import { useEffect, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { MoonPhaseResults } from "@/components/tools/astronomy/moon-phase-results";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { ZONE_IDS } from "@/lib/tool-kit/astronomy/places";
import {
  isKnownZone,
  nowInZone,
  parseClock,
  parseIsoDay,
  zonedToInstant,
} from "@/lib/tool-kit/astronomy/zone";

/** English strings. A translated content file overrides these through `ui`. */
export const MOON_PHASE_CALCULATOR_STRINGS = {
  date: "Date",
  time: "Time",
  zone: "Time zone",
  now: "Set to now",
  hemisphere: "View from",
  northern: "Northern hemisphere",
  southern: "Southern hemisphere",
  invalid: "Enter a valid date and time.",
  results: "The Moon on {date}",
  illumination: "Illuminated",
  age: "Age of the Moon",
  ageValue: "{days} days",
  cycle: "Through the lunar cycle",
  distance: "Distance from Earth",
  distanceValue: "{km} km",
  elongation: "Angle from the Sun",
  degrees: "{value}°",
  waxing: "Waxing: growing toward full moon.",
  waning: "Waning: shrinking toward new moon.",
  sinceNew: "The last new moon was {when}.",
  phases: {
    new: "New Moon",
    "waxing-crescent": "Waxing Crescent",
    "first-quarter": "First Quarter",
    "waxing-gibbous": "Waxing Gibbous",
    full: "Full Moon",
    "waning-gibbous": "Waning Gibbous",
    "last-quarter": "Last Quarter",
    "waning-crescent": "Waning Crescent",
  },
  upcomingHeading: "The next four main phases",
  phaseColumn: "Phase",
  whenColumn: "Date and time",
  inColumn: "From now",
  inDays: "in {days} days",
  calendarHeading: "{month} calendar",
  calendarNote:
    "Each day shows the Moon at noon in the chosen time zone. A marked day is the one on which the exact moment of that phase falls.",
  illuminationShort: "{percent}%",
  copy: "Copy result",
  copied: "Copied!",
  summary: "Moon on {date}: {phase}, {percent}% illuminated, {days} days old.",
  accuracy:
    "Phase times follow Jean Meeus's Astronomical Algorithms and agree with the US Naval Observatory to under a minute. Illumination is the geometric fraction of the disc; the Moon's age here is the time since the true new moon, which is shorter or longer than the 29.53-day average.",
} as const;

export type MoonPhaseStrings = typeof MOON_PHASE_CALCULATOR_STRINGS;

interface Form {
  date: string;
  time: string;
  zone: string;
  hemisphere: "northern" | "southern";
}

const INITIAL: Form = {
  date: "2026-10-26",
  time: "12:00",
  zone: "UTC",
  hemisphere: "northern",
};

export default function MoonPhaseCalculatorWidget() {
  const strings = useToolUi(MOON_PHASE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const [form, setForm] = useState<Form>(INITIAL);

  // Start from the current moment in the reader's own time zone, once on their machine.
  useEffect(() => {
    const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    setForm((current) => {
      const useZone = isKnownZone(zone) ? zone : current.zone;
      return { ...current, zone: useZone, ...nowInZone(useZone) };
    });
  }, []);

  const patch = (next: Partial<Form>) =>
    setForm((current) => ({ ...current, ...next }));
  const day = parseIsoDay(form.date);
  const clock = parseClock(form.time);
  const instant =
    day === null || clock === null
      ? null
      : zonedToInstant({ ...day, ...clock }, form.zone);
  const zones = ZONE_IDS.includes(form.zone)
    ? ZONE_IDS
    : [...ZONE_IDS, form.zone];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="mp-date"
          label={strings.date}
          onChange={(date) => patch({ date })}
          type="date"
          value={form.date}
        />
        <TextField
          id="mp-time"
          label={strings.time}
          onChange={(time) => patch({ time })}
          type="time"
          value={form.time}
        />
        <SelectField
          id="mp-zone"
          label={strings.zone}
          onChange={(zone) => patch({ zone })}
          options={zones.map((zone) => ({ value: zone, label: zone }))}
          value={form.zone}
        />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <PillGroup
          label={strings.hemisphere}
          name="mp-hemisphere"
          onChange={(hemisphere) => patch({ hemisphere })}
          options={[
            { value: "northern", label: strings.northern },
            { value: "southern", label: strings.southern },
          ]}
          value={form.hemisphere}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => patch(nowInZone(form.zone))}
          type="button"
        >
          {strings.now}
        </button>
      </div>
      <div aria-live="polite" className="space-y-6">
        {instant === null || day === null ? (
          <ErrorNotice>{strings.invalid}</ErrorNotice>
        ) : (
          <MoonPhaseResults
            day={day}
            instant={instant}
            locale={locale}
            southern={form.hemisphere === "southern"}
            strings={strings}
            zone={form.zone}
          />
        )}
      </div>
      <Notice>{strings.accuracy}</Notice>
    </div>
  );
}
